import { PageTitle, useIntl } from "@metsooutotec/modes-react-components"
import { observer } from "mobx-react-lite"
import { FC, useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { getAllocatedSensors } from "../../stock-managment/stock-management-helper"
import { SDASensor } from "features/iot/stock-management/models/sensor-dispatch-allocation"
import stockManagementStore from "features/iot/stock-management/stores/stock-management-store"
import sensorBatchStore from "features/iot/sensor-batch/stores/sensor-batch-store"
import customerStore from "features/organizations/customers/store/customer-store"
import ListView, { TemplateItem } from "components/utils/list-view/list-view"
import { convertDateToDDMMMYY } from "components/utils/date-formatter-utils"
import LowSpinner from "components/common/low-spinner"
import { NotFound } from "components/utils/not-found"
import { itemViewListContainerStyles } from "components/common-style/common-style"

interface tableSensorModel extends SDASensor {
    id: string
    customerName: string
    macAddress: string
    siteName: string
    allocatedDate: Date
}
const AllocatedSensorsListView: FC = observer(() => {
    const { formatMessage } = useIntl()
    const navigate = useNavigate()
    const { partNumber, customerName, siteName } = useParams()
    const { sensorBatches } = sensorBatchStore
    const [listOfSensors, setListOfSensors] = useState<tableSensorModel[]>([])

    const title = `Allocated Sensors For PartNumber: ${partNumber}`

    const { customers, loadAllCustomers } = customerStore

    /**
     * useEffect hook to load all customers when the component mounts or when the customers list is empty.
     * It ensures that the customer data is fetched and available for use in the component.
     */
    useEffect(() => {
        if (!customers.length) {
            loadAllCustomers()
        }
    }, [])

    useEffect(() => {
        if (partNumber && sensorBatches.length > 0) {
            // Fetch allocated sensors based on the part number.
            const allocatedSensors: SDASensor[] = getAllocatedSensors(partNumber)

            // Fetch IDs of sensors matching the criteria.
            const matchedSensorMacAddresses: string[] = allocatedSensors.map(s => s.macAddress)

            // Filter the allocatedSensors to include only those whose IDs are in matchedSensorIds.
            const filteredData = allocatedSensors.filter(sensor => matchedSensorMacAddresses.includes(sensor.macAddress))
            const listOfSensors: tableSensorModel[] = filteredData.map(s => ({
                ...s,
                id: s.macAddress,
                customerName: stockManagementStore.getSensorCustomerNameByMacAddress(s.macAddress) || "",
                siteName: stockManagementStore.getSensorSiteNameByMacAddress(s.macAddress) || "",
                allocatedDate: s.allocationDate || new Date(),
            }))
            setListOfSensors(listOfSensors)
        }
    }, [partNumber, customerName, siteName])

    const breadcrumbsPath = [
        {
            text: formatMessage("stockManagement.title"),
            to: "/stock-management",
        },
        {
            text: formatMessage("stockAllocation.title"),
            to: `/stock-management/stock-allocation/${partNumber}`,
        },
        {
            text: partNumber as string,
            to: `/stock-management/stock-allocation/${partNumber}`,
        },
    ]

    const template: TemplateItem<tableSensorModel>[] = [
        {
            headingKey: "headings.serialNumber",
            cellFunc: (a: tableSensorModel) => a.serialNumber,
            isSearchable: true,
        },
        {
            headingKey: "headings.macAddress",
            cellFunc: (a: tableSensorModel) => a.macAddress,
            isSearchable: true,
        },
        {
            headingKey: "headings.customer",
            cellFunc: (a: tableSensorModel) => a.customerName,
        },
        {
            headingKey: "headings.site",
            cellFunc: (a: tableSensorModel) => a.siteName,
        },
        {
            headingKey: "headings.allocationDate",
            cellFunc: (a: tableSensorModel) => {
                return convertDateToDDMMMYY(a.allocatedDate)
            },
            isSearchable: true,
        },
    ]

    return sensorBatches.length < 1 ? (
        <LowSpinner id={"sensorBatch.loading"} />
    ) : !listOfSensors ? (
        <NotFound id={title} />
    ) : (
        <>
            <PageTitle breadcrumbsPath={breadcrumbsPath} onPathChange={navigate} title={title} />
            <div style={itemViewListContainerStyles}>
                <ListView data={listOfSensors || []} noneYetKey='sensor.notFound' template={template} />
            </div>
        </>
    )
})

export default AllocatedSensorsListView
